import { getCurrentRegion } from './api';
import { getLanguagesForCountry } from './countryLanguageMap';
import { getLanguageOrderForCountry } from './countryLanguageOrderMap';
import { I18N_FALLBACK_LANGUAGE, I18N_SUPPORTED_LANGUAGES } from '../i18nConfig';

const STORAGE_KEY = 'i18nextLng';
const LANGUAGE_ORDER_KEY = 'i18nextLanguageOrder';

function resolveSupportedLanguage(lang: string): string | null {
  const raw = (lang || '').trim();
  if (!raw) return null;

  const supported = I18N_SUPPORTED_LANGUAGES as readonly string[];
  const supportedByLower = new Map(supported.map((l) => [l.toLowerCase(), l] as const));

  const lower = raw.toLowerCase();

  // Exact match (case-insensitive)
  const exact = supportedByLower.get(lower);
  if (exact) return exact;

  // Match by base language (e.g. fr-FR -> fr, zh -> zh-CN)
  const base = lower.split('-')[0];
  const baseExact = supportedByLower.get(base);
  if (baseExact) return baseExact;

  for (const candidate of supported) {
    if (candidate.toLowerCase().split('-')[0] === base) {
      return candidate;
    }
  }

  return null;
}

/**
 * Stores the language order in localStorage.
 */
function storeLanguageOrder(order: string[]): void {
  try {
    localStorage.setItem(LANGUAGE_ORDER_KEY, JSON.stringify(order));
  } catch {
    // localStorage might not be available
  }
}

/**
 * Gets the language order from localStorage if it exists.
 */
function getStoredLanguageOrder(): string[] | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_ORDER_KEY);
    if (stored) {
      const order = JSON.parse(stored);
      if (Array.isArray(order)) {
        return order;
      }
    }
  } catch {
    // localStorage might not be available or JSON parsing failed
  }
  return null;
}

/**
 * Gets the language from localStorage if it exists and is supported.
 */
function getStoredLanguage(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return resolveSupportedLanguage(stored);
    }
  } catch {
    // localStorage might not be available
  }
  return null;
}

/**
 * Stores the language in localStorage.
 */
function storeLanguage(lang: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // localStorage might not be available
  }
}

/**
 * Gets the language from URL query string parameter 'lng'.
 */
function getLanguageFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const params = new URLSearchParams(window.location.search);
    const lng = params.get('lng');
    if (lng) {
      const resolved = resolveSupportedLanguage(lng);
      if (resolved) {
        return resolved;
      }
    }
  } catch {
    // URL parsing might fail
  }
  return null;
}

/**
 * Gets the system language (OS language preference).
 */
function getSystemLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;
  
  try {
    // navigator.language is the OS/system language preference
    const systemLang = navigator.language;
    if (systemLang) {
      const resolved = resolveSupportedLanguage(systemLang);
      if (resolved) {
        return resolved;
      }
    }
  } catch {
    // navigator might not be available
  }
  return null;
}

/**
 * Gets the browser language (browser UI language).
 */
function getBrowserLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;
  
  try {
    // Check all browser languages
    const languages = navigator.languages || [navigator.language];
    for (const lang of languages) {
      const resolved = resolveSupportedLanguage(lang);
      if (resolved) {
        return resolved;
      }
    }
  } catch {
    // navigator might not be available
  }
  return null;
}

/**
 * Gets the language based on the user's country from the API.
 * This is an async operation that fetches the country from the backend.
 */
async function getLanguageFromCountry(): Promise<string | null> {
  try {
    const regionData = await getCurrentRegion();
    const countryCode = regionData?.userCountry;
    
    if (countryCode) {
      const langs = getLanguagesForCountry(countryCode);
      for (const lang of langs) {
        const resolved = resolveSupportedLanguage(lang);
        if (resolved) {
          return resolved;
        }
      }

      return I18N_FALLBACK_LANGUAGE;
    }
  } catch {
    // API call might fail
  }
  return null;
}

/**
 * Ensures the country-based language-switcher ordering is stored, fetching the user's country from
 * the backend when it isn't yet. Runs in the background: it must never block rendering (see
 * detectLanguage). Falls back to alphabetical order when the country can't be determined.
 */
async function ensureLanguageOrderStored(): Promise<void> {
  if (getStoredLanguageOrder()) {
    return;
  }

  try {
    const regionData = await getCurrentRegion();
    const countryCode = regionData?.userCountry;

    if (countryCode) {
      storeLanguageOrder(getLanguageOrderForCountry(countryCode));
    } else {
      storeLanguageOrder([...I18N_SUPPORTED_LANGUAGES]);
    }
  } catch {
    storeLanguageOrder([...I18N_SUPPORTED_LANGUAGES]);
  }
}

/**
 * Detects the user's preferred language following a priority order:
 * 1. Language from localStorage (i18nextLng)
 * 2. Language from URL query parameter (lng)
 * 3. System language (OS preference)
 * 4. Browser language
 * 5. Language from user's country (via API)
 * 6. Default to English
 * 
 * The detected language is persisted to localStorage.
 * Also stores the language order based on the user's country for the language switcher.
 * 
 * @returns Promise that resolves to the detected language code
 */
export async function detectLanguage(): Promise<string> {
  // Bootstrap the language-switcher ordering in the background. It only affects the switcher's
  // display order - never the detected language - and the backend round trip it needs (region
  // lookup, possibly against a cold function) was gating the app's FIRST RENDER for every new
  // visitor, directly inflating LCP. Fire-and-forget instead.
  void ensureLanguageOrderStored();

  // Priority 1: Check localStorage
  const storedLang = getStoredLanguage();
  if (storedLang) {
    return storedLang;
  }

  // Priority 2: Check URL query parameter
  const urlLang = getLanguageFromUrl();
  if (urlLang) {
    storeLanguage(urlLang);
    return urlLang;
  }

  // Priority 3: Check system language
  const systemLang = getSystemLanguage();
  if (systemLang) {
    storeLanguage(systemLang);
    return systemLang;
  }

  // Priority 4: Check browser language
  const browserLang = getBrowserLanguage();
  if (browserLang) {
    storeLanguage(browserLang);
    return browserLang;
  }

  // Priority 5: Check country-based language (async)
  const countryLang = await getLanguageFromCountry();
  if (countryLang) {
    storeLanguage(countryLang);
    return countryLang;
  }

  // Priority 6: Default to English
  const defaultLang = I18N_FALLBACK_LANGUAGE;
  storeLanguage(defaultLang);
  return defaultLang;
}

/**
 * Gets the stored language order from localStorage.
 * Returns the order if it exists, otherwise returns null.
 * 
 * @returns Array of language codes in priority order or null
 */
export function getStoredLanguageOrderForSwitcher(): string[] | null {
  return getStoredLanguageOrder();
}

/**
 * Synchronous version of language detection that doesn't include the country-based detection.
 * Use this when you need immediate language detection without async operations.
 * 
 * @returns The detected language code
 */
export function detectLanguageSync(): string {
  // Priority 1: Check localStorage
  const storedLang = getStoredLanguage();
  if (storedLang) {
    return storedLang;
  }

  // Priority 2: Check URL query parameter
  const urlLang = getLanguageFromUrl();
  if (urlLang) {
    storeLanguage(urlLang);
    return urlLang;
  }

  // Priority 3: Check system language
  const systemLang = getSystemLanguage();
  if (systemLang) {
    storeLanguage(systemLang);
    return systemLang;
  }

  // Priority 4: Check browser language
  const browserLang = getBrowserLanguage();
  if (browserLang) {
    storeLanguage(browserLang);
    return browserLang;
  }

  // Priority 5: Default to English (country check skipped in sync version)
  const defaultLang = I18N_FALLBACK_LANGUAGE;
  storeLanguage(defaultLang);
  return defaultLang;
}
